/**
 * Money and dates, formatted once for the whole module.
 *
 * Before this file the module built currency eleven different ways — `${'$'}${v.toLocaleString()}`,
 * `Intl.NumberFormat('en-AU', …)`, and a `${'$'}${x}` on a value that already carried its own dollar sign
 * (the v1.2.1 changelog records fixing a duplicated dollar sign on the detail page). One helper means
 * one answer.
 */

/** `12500` → `$12,500`. Whole dollars: a pipeline reads better without cents. */
export const money = (value?: number | null): string =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 })
    .format(value ?? 0);

/** `1234.5` → `$1,234.50`. For a team member's share, where the cents are somebody's pay. */
export const moneyExact = (value?: number | null): string =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .format(value ?? 0);

/**
 * A date a person reads: `12 Dec 2026`.
 *
 * Brisbane golden rule (vault/how-to/date-standards.md): the server stores an instant in UTC and the
 * screen shows it in the tenant's own zone. Australia/Brisbane has no daylight saving, so pinning the
 * zone here keeps a date from sliding a day for a reader whose laptop is set elsewhere — which is what
 * a bare `toLocaleDateString()` did on every page in this module.
 */
export const onDate = (value?: string | Date | null): string => {
  if (!value) return "Not set";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "Not set";
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric", timeZone: "Australia/Brisbane" });
};

/** `12 Dec 2026, 2:30 pm` — for an activity log, where the time of day matters. */
export const onDateTime = (value?: string | Date | null): string => {
  if (!value) return "Not set";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "Not set";
  return d.toLocaleString("en-AU", {
    day: "numeric", month: "short", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Australia/Brisbane",
  });
};

/** The `YYYY-MM-DD` an `<input type="date">` wants, read in Brisbane so the field shows the stored day. */
export const dateInputValue = (value?: string | Date | null): string => {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  // en-CA renders ISO order (2026-12-12); the explicit zone stops a UTC instant late in the day
  // from showing as the day before for a reader west of Brisbane.
  return d.toLocaleDateString("en-CA", { timeZone: "Australia/Brisbane" });
};

/** How a due date reads against today: "3 days overdue", "Due today", "Due in 5 days". */
export const dueWords = (value?: string | Date | null): { text: string; overdue: boolean; soon: boolean } => {
  if (!value) return { text: "No due date", overdue: false, soon: false };
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return { text: "No due date", overdue: false, soon: false };
  // Count Brisbane calendar days, the same zone `onDate` prints in, so the words and the date agree.
  const brisbaneDay = (x: Date) => Date.parse(dateInputValue(x));
  const days = Math.round((brisbaneDay(d) - brisbaneDay(new Date())) / 86400000);
  if (days < 0) return { text: `${Math.abs(days)} ${Math.abs(days) === 1 ? "day" : "days"} overdue`, overdue: true, soon: false };
  if (days === 0) return { text: "Due today", overdue: false, soon: true };
  if (days <= 3) return { text: `Due in ${days} ${days === 1 ? "day" : "days"}`, overdue: false, soon: true };
  return { text: `Due ${onDate(d)}`, overdue: false, soon: false };
};
